/**
 * Выравнивание фото перед загрузкой: рамка фиксированной пропорции, фото под
 * ней двигается и масштабируется.
 *
 * Зачем это вообще. Сервер приводит фото акима к единому размеру (FR-4.7) и без
 * рамки кадрирует ПО ЦЕНТРУ. Центр — безопасное значение по умолчанию, но на
 * реальных снимках (групповое фото, портрет со смещением, скан с полями) он
 * режет лицо. Поэтому кадр выбирает человек.
 *
 * Наружу отдаётся не картинка, а ПРЯМОУГОЛЬНИК в пикселях исходника: режет
 * сервер. Если бы резал браузер, оригинал пережимался бы дважды, а качество
 * зависело бы от устройства оператора.
 *
 * Геометрия. Фото лежит в вьюпорте с масштабом `scale` и смещением `offset`
 * (левый верхний угол фото относительно левого верхнего угла вьюпорта, в
 * пикселях вьюпорта). Тогда видимая область исходника —
 * `(-offset / scale, размер вьюпорта / scale)`. Инвариант: фото всегда
 * ПОКРЫВАЕТ вьюпорт, поэтому `scale >= cover` и смещение прижато к краям —
 * пустых полей в кадре быть не может.
 */
import { Loader2, Move, ZoomIn } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import type { PhotoCrop } from '../../../../requests/input_data.request';

/** Ширина рамки в CSS-пикселях; высота — из пропорции. */
const VIEWPORT_WIDTH = 288;
/** Во сколько раз максимум можно приблизить относительно «покрыть рамку». */
const MAX_ZOOM = 4;

export interface PhotoCropDialogProps {
  /** Выбранный оператором файл. Диалог открыт, пока он не null. */
  file: File | null;
  /** Пропорция рамки (ш, в) — её присылает сервер вместе с прошлой загрузкой. */
  aspect: [number, number];
  /** Идёт загрузка на сервер — кнопки заблокированы. */
  busy: boolean;
  onCancel: () => void;
  onApply: (crop: PhotoCrop) => void;
}

interface Natural {
  width: number;
  height: number;
}

export const PhotoCropDialog = ({
  file,
  aspect,
  busy,
  onCancel,
  onApply,
}: PhotoCropDialogProps) => {
  const { t } = useTranslation();
  const viewportHeight = Math.round((VIEWPORT_WIDTH * aspect[1]) / aspect[0]);

  const [url, setUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState<Natural | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  // Ссылка на файл живёт ровно столько, сколько открыт диалог: без revoke
  // каждый повторный выбор файла подтекал бы блобом на всю сессию вкладки.
  useEffect(() => {
    if (file === null) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    setNatural(null);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  /** Минимальный масштаб — «покрыть рамку»: меньше нельзя, будут пустые поля. */
  const minScale = useMemo(() => {
    if (natural === null) return 1;
    return Math.max(VIEWPORT_WIDTH / natural.width, viewportHeight / natural.height);
  }, [natural, viewportHeight]);

  const clampOffset = useCallback(
    (next: { x: number; y: number }, atScale: number, size: Natural) => ({
      x: Math.min(0, Math.max(VIEWPORT_WIDTH - size.width * atScale, next.x)),
      y: Math.min(0, Math.max(viewportHeight - size.height * atScale, next.y)),
    }),
    [viewportHeight],
  );

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    // naturalWidth = 0 у ещё не декодированной картинки; тогда брать нечего.
    const size = {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
    if (size.width === 0 || size.height === 0) return;
    const cover = Math.max(
      VIEWPORT_WIDTH / size.width,
      viewportHeight / size.height,
    );
    setNatural(size);
    setScale(cover);
    // Стартуем по центру — то же, что сделал бы сервер без рамки.
    setOffset({
      x: (VIEWPORT_WIDTH - size.width * cover) / 2,
      y: (viewportHeight - size.height * cover) / 2,
    });
  };

  /** Зум вокруг центра рамки: иначе фото «убегает» из кадра при каждом шаге. */
  const zoomTo = (next: number) => {
    if (natural === null) return;
    const target = Math.min(Math.max(next, minScale), minScale * MAX_ZOOM);
    const ratio = target / scale;
    const centerX = VIEWPORT_WIDTH / 2;
    const centerY = viewportHeight / 2;
    setScale(target);
    setOffset(
      clampOffset(
        {
          x: centerX - (centerX - offset.x) * ratio,
          y: centerY - (centerY - offset.y) * ratio,
        },
        target,
        natural,
      ),
    );
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (natural === null) return;
    drag.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const from = drag.current;
    if (from === null || natural === null) return;
    setOffset(
      clampOffset(
        { x: event.clientX - from.x, y: event.clientY - from.y },
        scale,
        natural,
      ),
    );
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  /** Видимая область в пикселях ИСХОДНИКА — это и есть рамка для сервера.
   *
   * `Math.max(0, …)` не только страхует от отрицательных координат (смещение
   * прижато к краям, так что их и не должно быть), но и убирает `-0`: при
   * нулевом смещении делением получается именно он, и в запрос уходило бы
   * `crop_y=-0`.
   */
  const crop = (): PhotoCrop => ({
    x: Math.max(0, -offset.x / scale),
    y: Math.max(0, -offset.y / scale),
    width: VIEWPORT_WIDTH / scale,
    height: viewportHeight / scale,
  });

  const ready = natural !== null;

  return (
    <Dialog
      open={file !== null}
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-md" data-testid="photo-crop">
        <DialogHeader>
          <DialogTitle>{t('adminInput.photoCropTitle')}</DialogTitle>
          <DialogDescription>{t('adminInput.photoCropHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3">
          <div
            role="presentation"
            data-testid="photo-crop-viewport"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onWheel={(event) => {
              event.preventDefault();
              zoomTo(scale * (event.deltaY < 0 ? 1.1 : 1 / 1.1));
            }}
            className="relative overflow-hidden rounded-md border border-surface-border bg-surface-muted"
            style={{
              width: VIEWPORT_WIDTH,
              height: viewportHeight,
              cursor: ready ? 'grab' : 'default',
              touchAction: 'none',
            }}
          >
            {url !== null && (
              <img
                src={url}
                alt={t('adminInput.photoCropTitle')}
                onLoad={handleLoad}
                draggable={false}
                data-testid="photo-crop-image"
                style={{
                  position: 'absolute',
                  left: offset.x,
                  top: offset.y,
                  width: natural === null ? 'auto' : natural.width * scale,
                  height: natural === null ? 'auto' : natural.height * scale,
                  userSelect: 'none',
                }}
              />
            )}
            {!ready && (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="size-5 animate-spin text-text-muted" />
              </span>
            )}
          </div>

          <label className="flex w-full items-center gap-2 text-xs text-text-muted">
            <ZoomIn className="size-4 shrink-0" />
            <input
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              disabled={!ready}
              value={ready ? scale / minScale : 1}
              aria-label={t('adminInput.photoCropZoom')}
              data-testid="photo-crop-zoom"
              onChange={(event) => zoomTo(minScale * Number(event.target.value))}
              className="w-full"
            />
          </label>
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            <Move className="size-3.5" />
            {t('adminInput.photoCropDrag')}
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!ready || busy}
            data-testid="photo-crop-apply"
            onClick={() => onApply(crop())}
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('adminInput.photoCropApply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
